import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/lib/toast';
import api from '@/lib/api';
import { usePageLibraryStore } from '@/store/pageLibraryStore';
import { useBlockStore } from '@/store/blockStore';

interface SavePageToLibraryDialogProps {
  /** De pagina die je bewaart; `null` sluit de dialoog. */
  page: { blockId: string; defaultName: string } | null;
  quoteId: string;
  onClose: () => void;
  /** Zet openstaande wijzigingen op de server, zodat de bibliotheek de laatste versie krijgt. */
  flushChanges: () => Promise<boolean>;
}

export default function SavePageToLibraryDialog({
  page,
  quoteId,
  onClose,
  flushChanges,
}: SavePageToLibraryDialogProps) {
  const libraryPages = usePageLibraryStore((state) => state.pages);
  const fetchPages = usePageLibraryStore((state) => state.fetchPages);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!page) return;
    setName(page.defaultName);
    setCategory('');
    setDescription('');
  }, [page]);

  const categories = useMemo(
    () =>
      [...new Set(libraryPages.map((p) => p.category?.trim()).filter((c): c is string => Boolean(c)))].sort(
        (a, b) => a.localeCompare(b, 'nl')
      ),
    [libraryPages]
  );

  const handleSave = async () => {
    if (!page || !name.trim()) return;
    setSaving(true);
    try {
      if (!(await flushChanges())) {
        toast.error('Niet opgeslagen: de laatste wijzigingen op deze pagina konden niet worden bewaard', {
          error: useBlockStore.getState().lastBlockSaveError,
        });
        return;
      }
      const saved = await api.createTemplateFromQuote({
        quoteId,
        name: name.trim(),
        category: category.trim() || undefined,
        description: description.trim() || undefined,
        blockIds: [page.blockId],
        kind: 'page',
      });
      await fetchPages();
      toast.success(`‘${saved.name}’ staat in de bibliotheek`);
      onClose();
    } catch (error) {
      toast.error('Opslaan in bibliotheek mislukt', { error });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(page)} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Opslaan in bibliotheek</DialogTitle>
          <DialogDescription>
            Daarna voeg je deze pagina in elke offerte in via Pagina toevoegen. Elke offerte krijgt een
            eigen kopie; deze offerte blijft zoals hij is.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="libraryPageName">Naam</Label>
            <Input
              id="libraryPageName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              placeholder="Bijv. Over UBO"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="libraryPageCategory">Categorie (optioneel)</Label>
            <Input
              id="libraryPageCategory"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={saving}
              placeholder="Bijv. Werkwijze of Voorwaarden"
              list="libraryPageCategories"
            />
            <datalist id="libraryPageCategories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label htmlFor="libraryPageDescription">Beschrijving (optioneel)</Label>
            <textarea
              id="libraryPageDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={saving}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Wanneer gebruik je deze pagina?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuleren
          </Button>
          <Button onClick={() => void handleSave()} disabled={!name.trim() || saving}>
            {saving ? 'Opslaan...' : 'Opslaan in bibliotheek'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
